import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, map, Observable } from 'rxjs';
import { environment } from '@firebird-web/shared-config';
import { CommonUtils } from '@firebird-web/shared-utils';
import {
  MultiplePermissions,
  Permissions,
  SinglePermission,
  PermissionCheck,
} from '@firebird-web/acl';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  usersPermissions: any = {};
  private permissions$: BehaviorSubject<string[] | Record<string, unknown>> =
    new BehaviorSubject(this.usersPermissions);

  constructor(private readonly http: HttpClient) {}

  public initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      const endPointUrl = `${environment.apiDomain}/api/v1/permissions/get-permissions`;
      try {
        firstValueFrom(this.http.get<string[]>(endPointUrl)).then((data) => {
          if (Array.isArray(data)) {
            this.usersPermissions = this.convertPathsToObject(data);
            this.permissions$ = new BehaviorSubject(this.usersPermissions);
            resolve();
          }
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching permissions:', error);
        reject();
      }
    });
  }

  getPermissions() {
    return this.usersPermissions || {};
  }

  getPermissionsAsync() {
    return this.permissions$;
  }

  public isViewPermittedAsync(
    permission: Permissions,
    key = 'View'
  ): Observable<boolean> {
    return this.permissions$.pipe(
      map((userPermissions) =>
        this.isPermitted(permission, key, userPermissions)
      )
    );
  }

  public isPermitted(
    permissions: Permissions,
    key = 'View',
    userPermissions = this.getPermissions()
  ): boolean {
    if (!userPermissions?.[key]) {
      return false;
    }

    if (CommonUtils.isMatrix(permissions)) {
      return (permissions as MultiplePermissions).some((permission) =>
        this.isPermissionExist(permission, key, userPermissions)
      );
    }

    return this.isPermissionExist(
      permissions as SinglePermission,
      key,
      userPermissions
    );
  }

  public areMultiplePermissionsGranted(
    permissionChecks: PermissionCheck[],
    userPermissions = this.getPermissions()
  ): boolean {
    return permissionChecks.every((check) =>
      this.isPermitted(check.permissions, check.key || 'View', userPermissions)
    );
  }

  public isPoolSetExist(poolset: string): boolean {
    return !!this.getPermissions().PoolSet?.[poolset];
  }

  public isForecastAnalysisGold(continent: string): boolean {
    const forecastAnalysis =
      this.getPermissions()?.['View']?.['Location_Page']?.['Forecast_Analysis'];
    return (
      typeof forecastAnalysis === 'object' &&
      forecastAnalysis !== null &&
      !!forecastAnalysis[continent]
    );
  }

  private isPermissionExist(
    permissions: SinglePermission,
    key: string,
    userPermissions: Record<string, unknown>
  ): boolean {
    return !!permissions.reduce((acc, item) => {
      if (typeof acc === 'boolean') {
        return acc;
      }

      if (Array.isArray(acc)) {
        return acc.includes(item);
      }

      if (key === 'PoolSet') {
        const keys = Object.keys(acc as Record<string, unknown>);

        return keys
          .filter((k) => k.includes(item))
          .some((k) => (acc as Record<string, unknown>)[k]);
      }

      if (typeof acc === 'object') {
        return acc?.[item as keyof typeof acc] ?? false;
      }

      return acc;
    }, userPermissions?.[key]);
  }

  getFirstAvailableContinent(
    permission: string[],
    key = 'View',
    userPermissions = this.getPermissions()
  ): string {
    const permittedRegionsObg = permission.reduce(
      (acc: unknown, item: string) => {
        return acc?.[item as keyof typeof acc] || ({} as object);
      },
      (userPermissions as Record<string, unknown>)?.[key]
    );

    return Object.keys(permittedRegionsObg as object)[0] ?? '';
  }

  private convertPathsToObject(
    paths: string[]
  ): Record<string, boolean | Record<string, boolean>> {
    const result = {};

    for (const path of paths) {
      const segments = path.split('__');
      let currentLevel: Record<string, boolean | Record<string, boolean>> =
        result;

      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        const isLastSegment = i === segments.length - 1;

        if (isLastSegment) {
          // If it's the last segment:
          // Only set to true if the current segment's path is not already an object.
          // This prevents overwriting an existing object (which is a parent to other leaves/branches)
          // with 'true' just because a shorter path ends here.
          if (!this.isObject(currentLevel[segment])) {
            currentLevel[segment] = true;
          }
          // If currentLevel[segment] is already an object (e.g., because a path like
          // 'View__Location_Page__Forecast_Analysis__NA' was processed before
          // 'View__Location_Page__Forecast_Analysis'), we do nothing here,
          // preserving the object structure.
        } else {
          // Not the last segment, so it must be an object to hold children.
          // If currentLevel[segment] doesn't exist yet, or if it was previously set to `true`
          // (because a shorter path ended at this segment, and now a longer path is extending through it),
          // we need to (re)initialize it as an object.
          if (
            !this.isObject(currentLevel[segment]) ||
            currentLevel[segment] === true
          ) {
            currentLevel[segment] = {};
          }
          // Move to the next level down.
          // The type assertion is safe because we've ensured it's an object.
          currentLevel = currentLevel[segment] as Record<
            string,
            boolean | Record<string, boolean>
          >;
        }
      }
    }

    return result;
  }

  private isObject(item: unknown): boolean {
    return (item &&
      typeof item === 'object' &&
      !Array.isArray(item)) as boolean;
  }
}
